import { useParams } from 'react-router-dom'

import { Box } from '@factorialco/deprecated-design-system'
import { AvailableModules, Features } from 'generated/featureDefinitions'
import {
  ContactsDetailsQuotesPageDocument,
  FinanceValueObjectsContactTypeEnum,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import { QuotesListTable } from 'modules/finance/components/Quotes/QuotesListTable'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import { ValidReasons } from 'modules/upsell/hooks/useCreateUpsellRequest'
import { UpsellingKit } from 'modules/upselling_kit/components'

interface ContactsDetailsQuotesPageProps {
  contactType: FinanceValueObjectsContactTypeEnum
}

const ContactsDetailsQuotesPage: React.FC<ContactsDetailsQuotesPageProps> = ({ contactType }) => {
  const { contactId } = useParams()
  const { company, access, canRequestUpsell } = useCurrentSession()

  const { data, loading, error } = useQuery(ContactsDetailsQuotesPageDocument, {
    variables: {
      companyId: company.id,
      featureIds: [Features.sales_quotes_management],
    },
  })

  const hasQuotesFeature = !!data?.companies.featureAvailabilitiesConnection.nodes.find(
    (feature) => feature.id === Features.sales_quotes_management
  )?.available

  const canSeeQuotesContent =
    data?.finance?._policies?.sidebarSeeFinanceQuotesSubLevelItem?.can ?? false
  const canSeeQuotesUpsell = !hasQuotesFeature && canRequestUpsell
  const canSeePage = contactType === 'client' && (canSeeQuotesContent || canSeeQuotesUpsell)
  const canCreateQuote = data?.finance?._policies?.quotesCreate?.can ?? false

  useEffect(() => {
    track(trackingEvents.accounting.viewContactDetailsPage, {
      view: 'quotes',
      quotes_upsell: canSeeQuotesUpsell,
    })
  }, [canSeeQuotesUpsell])

  return (
    <ResponsivePage.Body authorized={canSeePage} loading={loading} error={error}>
      {!hasQuotesFeature ? (
        <Box alignItems="center" justifyContent="center" style={{ height: '50vh' }}>
          <UpsellingKit.EmptyState
            moduleRequested={AvailableModules.sales}
            featureRequested={Features.sales_quotes_management}
            requestReason={ValidReasons.MissingAddon}
            requestSource="sales_from_contact_details_on_quotes_tab"
            title={i18n.t('finance.contacts.contact_details.quotes.upsell_title')}
            description={i18n.t('finance.contacts.contact_details.quotes.upsell_description')}
          />
        </Box>
      ) : (
        <QuotesListTable
          companyId={company.id}
          locale={access.locale}
          clientId={contactId ? Number(contactId) : undefined}
          canCreateQuote={canCreateQuote}
          showDealColumn
        />
      )}
    </ResponsivePage.Body>
  )
}

export default ContactsDetailsQuotesPage
